import { type HonoRequest } from "hono";
import { proxy } from "hono/proxy";

// Domain constants
const DOMAIN = {
  PROD_WATI: "astra.wati.io",
  PROD_ENGAGE: "app.engagechat.ai",
  DEV_WATI: "dev-astra.watiapp.io",
  DEV_ENGAGE: "dev-astra.engagechat.ai",
  DEV_BETA_WATI: "dev-beta-astra.watiapp.io",
  DEV_BETA_ENGAGE: "dev-beta-astra.engagechat.ai",
  QA_WATI: "qa-astra.watiapp.io",
  QA_ENGAGE: "qa-astra.engagechat.ai",
  STAGE_WATI: "stage-astra.watiapp.io",
  STAGE_ENGAGE: "stage-astra.engagechat.ai"
} as const;

// Internal service URLs
const INTERNAL_SERVICE = {
  PROD: "http://astra-gateway.astra.svc.cluster.local:8080",
  DEV: "http://astra-gateway.dev-astra.svc.cluster.local:8080",
  DEV_BETA: "http://astra-gateway.dev-beta-astra.svc.cluster.local:8080",
  QA: "http://astra-gateway.qa-astra.svc.cluster.local:8080",
  STAGE: "http://astra-gateway.astra.svc.cluster.local:8080"
} as const;

// External gateway URLs
const EXTERNAL_GATEWAY = {
  PROD: "https://astra-gateway.wati.io",
  DEV: "https://astra-gateway.watiapp.io",
  DEV_BETA: "https://dev-beta-astra-gateway.watiapp.io",
  DEV_BETA_TYPO: "https://dev-bate-astra-gateway.watiapp.io", // Note: typo in original
  QA: "https://qa-astra-gateway.watiapp.io",
  STAGE: "https://astra-gateway.wati.io"
} as const;

// WhatsApp Voice gateway URLs
const WHATSAPP_GATEWAY = {
  PROD: "http://astra-whatsappcall-gateway.astra.svc.cluster.local:8082",
  DEV: "http://astra-whatsappcall-gateway.dev-astra.svc.cluster.local:8082",
  QA: "http://astra-whatsappcall-gateway.qa-astra.svc.cluster.local:8082",
  STAGE: "http://astra-whatsappcall-gateway.astra.svc.cluster.local:8082"
} as const;

export const HostMap = {
  [DOMAIN.PROD_ENGAGE]: INTERNAL_SERVICE.PROD,
  [DOMAIN.PROD_WATI]: INTERNAL_SERVICE.PROD,
  [DOMAIN.DEV_ENGAGE]: INTERNAL_SERVICE.DEV,
  [DOMAIN.DEV_WATI]: INTERNAL_SERVICE.DEV,
  [DOMAIN.DEV_BETA_ENGAGE]: INTERNAL_SERVICE.DEV_BETA,
  [DOMAIN.DEV_BETA_WATI]: INTERNAL_SERVICE.DEV_BETA,
  [DOMAIN.QA_ENGAGE]: INTERNAL_SERVICE.QA,
  [DOMAIN.QA_WATI]: INTERNAL_SERVICE.QA,
  [DOMAIN.STAGE_WATI]: INTERNAL_SERVICE.STAGE,
  [DOMAIN.STAGE_ENGAGE]: INTERNAL_SERVICE.STAGE
};

export const GateWayHostmap = {
  [DOMAIN.PROD_WATI]: EXTERNAL_GATEWAY.PROD,
  [DOMAIN.PROD_ENGAGE]: EXTERNAL_GATEWAY.PROD,
  [DOMAIN.DEV_WATI]: EXTERNAL_GATEWAY.DEV,
  [DOMAIN.DEV_ENGAGE]: EXTERNAL_GATEWAY.DEV,
  [DOMAIN.DEV_BETA_ENGAGE]: EXTERNAL_GATEWAY.DEV_BETA,
  [DOMAIN.DEV_BETA_WATI]: EXTERNAL_GATEWAY.DEV_BETA_TYPO,
  [DOMAIN.QA_WATI]: EXTERNAL_GATEWAY.QA,
  [DOMAIN.QA_ENGAGE]: EXTERNAL_GATEWAY.QA,
  [DOMAIN.STAGE_WATI]: EXTERNAL_GATEWAY.STAGE,
  [DOMAIN.STAGE_ENGAGE]: EXTERNAL_GATEWAY.STAGE
};

// WhatsApp Voice Agent Gateway Host Map
export const WhatsAppVoiceHostMap = {
  [DOMAIN.PROD_WATI]: WHATSAPP_GATEWAY.PROD,
  [DOMAIN.PROD_ENGAGE]: WHATSAPP_GATEWAY.PROD,
  [DOMAIN.DEV_WATI]: WHATSAPP_GATEWAY.DEV,
  [DOMAIN.DEV_ENGAGE]: WHATSAPP_GATEWAY.DEV,
  [DOMAIN.DEV_BETA_WATI]: WHATSAPP_GATEWAY.DEV,
  [DOMAIN.DEV_BETA_ENGAGE]: WHATSAPP_GATEWAY.DEV,
  [DOMAIN.QA_WATI]: WHATSAPP_GATEWAY.QA,
  [DOMAIN.QA_ENGAGE]: WHATSAPP_GATEWAY.QA,
  [DOMAIN.STAGE_WATI]: WHATSAPP_GATEWAY.STAGE,
  [DOMAIN.STAGE_ENGAGE]: WHATSAPP_GATEWAY.STAGE
};

// Default value used when no match is found
export const GATE_API_HOST = "https://astra-gateway.watiapp.io";

export const WHATSAPP_VOICE_API_HOST =
  "https://astra-whatsappcall-gateway.watiapp.io";

export const getProxyHost = (hostname: string) => {
  return HostMap[hostname] || GATE_API_HOST;
};

export const getWhatsAppVoiceHost = (hostname: string) => {
  return WhatsAppVoiceHostMap[hostname] || WHATSAPP_VOICE_API_HOST;
};

export const proxyFactory = (req: HonoRequest, baseURL = "") => {
  // console.log("req: ", req);
  let path = req.path.slice(4).replace("/conversation/v1", "/api/v1");
  const urlObject = new URL(req.url);
  const hostname = urlObject.hostname;
  const baseUrl = baseURL || getProxyHost(hostname);

  // Add /api prefix for WhatsApp Voice Gateway  ps.  previous content will delete /api
  const isWhatsAppGateway = baseUrl.includes("whatsappcall-gateway");
  if (isWhatsAppGateway) {
    path = `/api${path}`;
  }

  const url = `${baseUrl}${path}${urlObject.search}`;
  // console.log("url:", url);

  // 创建 AbortController 用于超时控制
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, 30000); // 30秒超时

  const proxyPromise = proxy(url, {
    method: req.method,
    headers: req.raw.headers,
    body: req.raw.body,
    // @ts-ignore
    duplex: "half",
    signal: controller.signal
  });

  // 确保在请求完成后清理超时定时器
  return proxyPromise.finally(() => {
    clearTimeout(timeoutId);
  });
};
